import { SDK_VERSION } from './env';
import type {
  ClickPayload,
  ConversionPayload,
  ImpressionContext,
  ImpressionItem,
  ImpressionPayload
} from './types';

const IMPRESSION_DEBOUNCE_MS = 2_000;
const IMPRESSION_CAP = 20;

export class Transport {
  private readonly merchantId: string;
  private readonly apiUrlBase: string;
  private readonly makeImpressionPayload: (
    context: ImpressionContext,
    items: ImpressionItem[]
  ) => ImpressionPayload;
  private readonly canSend: () => boolean;
  private readonly buffer: ImpressionItem[] = [];
  private activeContext: ImpressionContext | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    merchantId: string,
    apiUrlBase: string,
    makeImpressionPayload: (context: ImpressionContext, items: ImpressionItem[]) => ImpressionPayload,
    canSend: () => boolean = () => true
  ) {
    this.merchantId = merchantId;
    this.apiUrlBase = apiUrlBase;
    this.makeImpressionPayload = makeImpressionPayload;
    this.canSend = canSend;
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pagehide', this.onUnload);
      window.addEventListener('beforeunload', this.onUnload);
    }
  }

  enqueueImpression(item: ImpressionItem, context: ImpressionContext): void {
    if (this.activeContext && !sameContext(this.activeContext, context)) this.flush();
    if (!this.activeContext) this.activeContext = snapshotContext(context);
    this.buffer.push(item.page === undefined ? { sku: item.sku, position: item.position } : { ...item });
    if (this.buffer.length >= IMPRESSION_CAP) {
      this.flush();
      return;
    }
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), IMPRESSION_DEBOUNCE_MS);
  }

  flush(useBeacon = false): void {
    if (!this.canSend()) {
      this.destroy();
      return;
    }
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (this.buffer.length === 0) return;

    const context = this.activeContext;
    if (!context) return;
    const items = this.buffer.splice(0, IMPRESSION_CAP);
    if (this.buffer.length === 0) this.activeContext = undefined;
    this.post('impressions', this.makeImpressionPayload(context, items), useBeacon);
  }

  sendClick(payload: ClickPayload): void {
    void this.post('click', payload);
  }

  sendConversion(payload: ConversionPayload): Promise<boolean> {
    return this.post('conversion', payload);
  }

  destroy(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.buffer.length = 0;
    this.activeContext = undefined;
    if (typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pagehide', this.onUnload);
      window.removeEventListener('beforeunload', this.onUnload);
    }
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'hidden') return;
    if (!this.canSend()) {
      this.destroy();
      return;
    }
    this.flush(true);
  };

  private readonly onUnload = (): void => {
    if (!this.canSend()) {
      this.destroy();
      return;
    }
    this.flush(true);
  };

  private async post(
    event: 'impressions' | 'click' | 'conversion',
    payload: ClickPayload | ConversionPayload | ImpressionPayload,
    useBeacon = false
  ): Promise<boolean> {
    const body = JSON.stringify(payload);
    const url = this.url(event);

    // sendBeacon cannot carry custom headers; X-Vidisape-SDK-Version is only
    // present on the keepalive fetch fallback path.
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return true;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Vidisape-SDK-Version': SDK_VERSION
        },
        body,
        keepalive: true
      });
      return response.status >= 200 && response.status < 300;
    } catch {
      // Tracking must not break storefront behavior.
      return false;
    }
  }

  private url(event: 'impressions' | 'click' | 'conversion'): string {
    return `${this.apiUrlBase}/api/v1/${encodeURIComponent(this.merchantId)}/events/${event}`;
  }
}

function sameContext(left: ImpressionContext, right: ImpressionContext): boolean {
  return (
    left.query_id === right.query_id &&
    left.session_id === right.session_id &&
    left.anonymous_user_id === right.anonymous_user_id &&
    left.user_id_hash === right.user_id_hash
  );
}

function snapshotContext(context: ImpressionContext): ImpressionContext {
  return context.user_id_hash === undefined ? { ...context } : { ...context, user_id_hash: context.user_id_hash };
}
